import { useState, useEffect } from "react";
import api from "../utils/api";
import apiV2 from "../utils/apiV2";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import ServiceToggle from "./ServiceToggle";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Copy, ExternalLink, Plus, X, Edit, Trash2, Image as ImageIcon, CheckCircle, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Anchor, Clock, Users, DollarSign, Settings, Code, Monitor, Calendar, Check, Box, Layers, Sparkles, Tag, MapPin } from "lucide-react";
import { toast } from "sonner";
import DashboardPageLayout from './DashboardPageLayout';
import PageGuide from './PageGuide';
import { GUIDE_CONTENT } from '../config/guideContent';
import FormSection from './FormSection';
import { PrimaryButton, SecondaryButton, SuccessButton } from './Buttons';
import StandardModal from './StandardModal';
import TimeSlotSelector from './TimeSlotSelector';
import MultiTimeSlotSelector from './MultiTimeSlotSelector';
import { BACKEND_URL, FRONTEND_URL, API_URL, WIDGET_PRODUCTION_URL } from '../config/api';
import { CATEGORY_OPTIONS } from '../constants/categories';
import CitySelect from './CitySelect';
import { getCityDisplayName } from '../constants/usCities';

// BACKEND_URL imported from config/api


// Team Member Selector Component
const TeamMemberSelectorInline = ({ linkedTeamMembers, onChange }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await api.get('/team');
        setTeamMembers(response.data || []);
      } catch (error) {
        console.error('Error fetching team members:', error);
        toast.error('Failed to load team members');
      } finally {
        setLoading(false);
      }
    };
    fetchTeamMembers();
  }, []);

  if (loading) {
    return <div className="text-sm text-gray-500 p-3">Loading team members...</div>;
  }

  if (teamMembers.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
        <strong>⚠️ No team members found.</strong> Please create team members in the Team tab first.
      </div>
    );
  }

  return (
    <>
      <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm text-purple-800 mb-3">
        <strong>How it works:</strong> Customers can pick their preferred person (like choosing "I want Sarah as my yoga instructor"). 
        If you don't link anyone, you'll assign after booking or use auto-scheduler.
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3 bg-gray-50">
        {teamMembers.map((member) => {
          const isSelected = (linkedTeamMembers || []).includes(member.id);
          return (
            <label
              key={member.id}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                isSelected 
                  ? 'bg-purple-100 border-2 border-purple-500' 
                  : 'bg-white border-2 border-gray-200 hover:border-purple-300'
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {
                  const newLinkedTeamMembers = isSelected
                    ? (linkedTeamMembers || []).filter(id => id !== member.id)
                    : [...(linkedTeamMembers || []), member.id];
                  onChange(newLinkedTeamMembers);
                }}
                className="w-5 h-5 text-purple-600"
              />
              <Users className="w-5 h-5 text-purple-600 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium">{member.name}</div>
                <div className="text-xs text-gray-600">
                  {member.email || 'No email'} • {member.roleName || 'No role'}
                </div>
              </div>
              {isSelected && <Check className="w-5 h-5 text-purple-600" />}
            </label>
          );
        })}
      </div>
      <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
        ✅ Selected {(linkedTeamMembers || []).length} team member{(linkedTeamMembers || []).length !== 1 ? 's' : ''}
      </p>
    </>
  );
};


const WidgetCustomization = ({ onNavigate }) => {
  const [showGuide, setShowGuide] = useState(() => {
    return localStorage.getItem('hideStepGuide') !== 'true';
  });

  // Listen for guide visibility changes
  useEffect(() => {
    const handleGuideChange = () => {
      setShowGuide(localStorage.getItem('hideStepGuide') !== 'true');
    };
    window.addEventListener('guideVisibilityChanged', handleGuideChange);
    return () => window.removeEventListener('guideVisibilityChanged', handleGuideChange);
  }, []);

  const [config, setConfig] = useState({
    primaryColor: "#2563EB",
    backgroundColor: "#FFFFFF",
    textColor: "#111111",
    logoUrl: "",
    companyName: "",
    welcomeText: "Book Your Experience",
    buttonText: "Book Now",
    buttonPosition: "bottom-right",
    widgetType: "embedded",
    enableCustomRequests: true
  });
  const [services, setServices] = useState([]);
  const [assets, setAssets] = useState([]); // Fetch assets for dropdowns
  const [roles, setRoles] = useState([]); // NEW: Fetch roles for dropdown
  const [orgSettings, setOrgSettings] = useState(null); // NEW: Org settings for auto-populate
  const [loading, setLoading] = useState(true);
  const [orgData, setOrgData] = useState(null);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceModalStep, setServiceModalStep] = useState(1);
  const totalServiceSteps = 3; // Simple 3-step flow
  const [serviceForm, setServiceForm] = useState({
    name: "",
    description: "",
    category: "", // Marketplace category (required)
    cityData: null, // City object { city, state, stateFullName }
    meetingLocation: "", // Specific address (required for marketplace)
    duration: 60,
    capacity: 6, // Legacy field for backward compatibility
    price: 150,
    timezone: "CST",
    emergencyContactNumber: "",
    successRedirectUrl: "",
    cancelRedirectUrl: "",
    images: [],
    availableTimeSlots: ["09:00", "11:00", "13:00", "15:00", "17:00"],
    associatedAssets: [], // Deprecated: Use capacitySource instead
    serviceType: "per_ticket", // Deprecated: Use bookingMode instead
    active: true,
    // NEW CAPACITY CONFIGURATION
    bookingMode: "per_ticket", // "per_ticket", "buyout", or "per_team_member"
    capacitySource: "assets", // "assets", "team_members", or "both"
    capacityPerResource: 12, // 6 students/instructor, 12 guests/asset, etc.
    requiredRole: "", // "captain", "instructor", "guide"
    requiredAssetType: "", // "boat", "room", "equipment"
    useMinimumCapacity: true, // Use smaller capacity when capacitySource="both"
    // BRONZE IMPLEMENTATION - Context-based booking
    requiresAsset: false, // Does this service NEED an asset assigned?
    requiresTeamMember: false, // Does this service NEED a team member assigned?
    linkedAssets: [], // Array of asset IDs that can fulfill this service
    linkedTeamMembers: [], // Array of team member IDs that can fulfill this service
    // MULTI-DAY SUPPORT
    isMultiDay: false, // Toggle for multi-day events
    durationDays: 1, // Number of days (1-7, or custom)
    startTime: "09:00", // Start time for multi-day events
    customQuestions: [], // [{ question, type, required }]
    addOns: [] // [{ id, name, price, description }]
  });
  const [previewWidget, setPreviewWidget] = useState("standard"); // standard, cards, button, link
  const [buttonPosition, setButtonPosition] = useState("bottom-right");
  const [servicesPage, setServicesPage] = useState(1);
  const SERVICES_PER_PAGE = 5;

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      // Fetch widget config
      const configResponse = await api.get('/widget/config');
      setConfig(configResponse.data);
      
      const orgId = configResponse.data.organizationId;
      
      if (orgId) {
        // Fetch org details
        try {
          const orgResponse = await api.get(`/organizations/${orgId}`);
          setOrgData({
            orgId: orgResponse.data.id,
            shortname: orgResponse.data.slug
          });
        } catch (orgError) {
          const userData = JSON.parse(localStorage.getItem('userData') || '{}');
          const orgSlug = localStorage.getItem('organizationSlug') || userData.organizationSlug;
          setOrgData({
            orgId: orgId,
            shortname: orgSlug || 'unknown'
          });
        }
        
        // Fetch services and assets
        await loadServices();
        await loadAssets();
        await loadRoles();
        await loadOrgSettings();
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      // Use v2 API for services (Postgres)
      const response = await apiV2.getServices();
      setServices(response.data?.services || response.data || []);
    } catch (error) {
      console.error("Error loading services:", error);
    }
  };

  const loadAssets = async () => {
    try {
      // Use v2 API for assets (Postgres)
      const response = await apiV2.getAssets();
      const assetsData = response.data?.assets || response.data || [];
      setAssets(assetsData.filter(v => v.active || v.isActive)); // Only show active assets
    } catch (error) {
      console.error("Error loading assets:", error);
    }
  };

  const loadRoles = async () => {
    try {
      // Use v2 API for roles (Postgres)
      const response = await apiV2.getRoles();
      setRoles(response.data?.roles || response.data || []);
    } catch (error) {
      console.error("Error loading roles:", error);
      setRoles([]);
    }
  };

  const loadOrgSettings = async () => {
    try {
      // Get current user's org ID
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = userData.organizationId;
      
      if (orgId) {
        const response = await api.get(`/organizations/${orgId}`);
        setOrgSettings(response.data);
      }
    } catch (error) {
      console.error("Error loading org settings:", error);
    }
  };

  // Calculate capacity based on selected assets (lowest capacity)
  const calculateCapacityFromAssets = (assetIds) => {
    if (!assetIds || assetIds.length === 0) return serviceForm.capacity;
    
    const selectedAssets = assets.filter(v => assetIds.includes(v.id));
    if (selectedAssets.length === 0) return serviceForm.capacity;
    
    return Math.min(...selectedAssets.map(v => v.capacity));
  };

  // Handle asset selection and auto-update capacity
  const handleAssetSelection = (assetId) => {
    let newAssets;
    if (serviceForm.associatedAssets.includes(assetId)) {
      // Remove asset
      newAssets = serviceForm.associatedAssets.filter(id => id !== assetId);
    } else {
      // Add asset
      newAssets = [...serviceForm.associatedAssets, assetId];
    }
    
    const newCapacity = calculateCapacityFromAssets(newAssets);
    setServiceForm({
      ...serviceForm,
      associatedAssets: newAssets,
      capacity: newCapacity || 1
    });
  };

  const handleSaveConfig = async () => {
    try {
      await api.put('/widget/config', config);
      toast.success("Widget settings saved!");
      reloadPreview();
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Failed to save settings");
    }
  };

  const reloadPreview = () => {
    const iframe = document.querySelector('#widget-preview');
    if (iframe) {
      iframe.src = iframe.src;
    }
  };

  const openServiceModal = (service = null) => {
    if (service) {
      setEditingService(service);
      // Parse city from service data
      let cityData = null;
      if (service.city && service.state) {
        cityData = { city: service.city, state: service.state, stateFullName: service.stateFullName || '' };
      }
      setServiceForm({
        name: service.name,
        description: service.description || "",
        category: service.category || "", // Marketplace category
        cityData: cityData, // City object
        meetingLocation: service.meetingLocation || "", // Specific address
        duration: service.duration,
        capacity: service.capacity || 6,
        price: service.price,
        timezone: service.timezone || orgSettings?.timezone || "CST",
        emergencyContactNumber: service.emergencyContactNumber || orgSettings?.emergencyPhoneForTickets || "",
        associatedAssets: service.associatedAssets || [],
        serviceType: service.serviceType || service.bookingMode || "per_ticket",
        successRedirectUrl: service.successRedirectUrl || "",
        cancelRedirectUrl: service.cancelRedirectUrl || "",
        images: service.images || [],
        availableTimeSlots: service.availableTimeSlots || ["09:00", "11:00", "13:00", "15:00", "17:00"],
        active: service.active !== undefined ? service.active : true,
        // NEW FIELDS
        bookingMode: service.bookingMode || service.serviceType || "per_ticket",
        capacitySource: service.capacitySource || "assets",
        capacityPerResource: service.capacityPerResource || 12,
        requiredRole: service.requiredRole || "",
        requiredAssetType: service.requiredAssetType || "",
        useMinimumCapacity: service.useMinimumCapacity !== undefined ? service.useMinimumCapacity : true,
        customQuestions: service.customQuestions || [],
        addOns: service.addOns || []
      });
    } else {
      setEditingService(null);
      setServiceForm({
        name: "",
        description: "",
        category: "", // Marketplace category
        cityData: null, // City object
        meetingLocation: "", // Specific address
        duration: 60,
        capacity: 6,
        price: 150,
        timezone: orgSettings?.timezone || "CST", // Auto-populate from org
        emergencyContactNumber: orgSettings?.emergencyPhoneForTickets || "", // Auto-populate from org
        associatedAssets: [],
        serviceType: "per_ticket",
        successRedirectUrl: "",
        cancelRedirectUrl: "",
        images: [],
        availableTimeSlots: ["09:00", "11:00", "13:00", "15:00", "17:00"],
        active: true,
        // NEW FIELDS
        bookingMode: "per_ticket",
        capacitySource: "assets",
        capacityPerResource: 12,
        requiredRole: "",
        requiredAssetType: "",
        useMinimumCapacity: true,
        customQuestions: [],
        addOns: []
      });
    }
    setServiceModalStep(1); // Reset to step 1
    setShowServiceModal(true);
  };

  const closeServiceModal = () => {
    setShowServiceModal(false);
    setServiceModalStep(1);
    setEditingService(null);
  };

  const nextServiceStep = () => {
    if (validateServiceStep()) {
      setServiceModalStep(prev => Math.min(prev + 1, totalServiceSteps));
    }
  };

  const prevServiceStep = () => {
    setServiceModalStep(prev => Math.max(prev - 1, 1));
  };

  const validateServiceStep = () => {
    switch (serviceModalStep) {
      case 1: // Basics
        if (!serviceForm.name.trim()) {
          toast.error('Please enter a service name');
          return false;
        }
        if (!serviceForm.price || serviceForm.price <= 0) {
          toast.error('Please enter a valid price');
          return false;
        }
        if (!serviceForm.emergencyContactNumber || serviceForm.emergencyContactNumber.trim() === '') {
          toast.error('Fall Back Booking Contact Number is required');
          return false;
        }
        return true;
      case 2: // Assets
        if (!serviceForm.linkedAssets || serviceForm.linkedAssets.length === 0) {
          toast.error('Please select at least one asset');
          return false;
        }
        return true;
      case 3: // Schedule
        if (!serviceForm.isMultiDay && serviceForm.availableTimeSlots.length === 0) {
          toast.error('Please add at least one available time slot');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSaveService = async () => {
    try {
      if (!serviceForm.name || !serviceForm.duration || !serviceForm.price) {
        toast.error("Please fill in all required fields");
        return;
      }

      // Category is required for marketplace
      if (!serviceForm.category) {
        toast.error("Please select a category for your experience");
        setServiceModalStep(1);
        return;
      }

      // City is required for marketplace
      if (!serviceForm.cityData) {
        toast.error("Please select a city for this experience");
        setServiceModalStep(1);
        return;
      }

      // Capacity validation
      if (!serviceForm.capacityPerResource || serviceForm.capacityPerResource <= 0) {
        toast.error("Please enter capacity per resource");
        return;
      }

      // Emergency contact is REQUIRED
      if (!serviceForm.emergencyContactNumber || serviceForm.emergencyContactNumber.trim() === '') {
        toast.error("Emergency contact number is required for customer support");
        return;
      }
      
      // ASSET SELECTION IS REQUIRED (new rule: SERVICE = LOGISTICS + ASSET + PRICE)
      if (!serviceForm.linkedAssets || serviceForm.linkedAssets.length === 0) {
        toast.error("Please select at least one asset. Every service needs an asset to define capacity.");
        setServiceModalStep(2); // Jump to asset selection step
        return;
      }

      // Time slots required for single-day events only
      if (!serviceForm.isMultiDay && serviceForm.availableTimeSlots.length === 0) {
        toast.error("Please add at least one available time slot");
        setServiceModalStep(3); // Jump to schedule step
        return;
      }

      if (!serviceForm.timezone) {
        toast.error("Please specify a timezone");
        return;
      }

      // Extract city data into separate fields
      const cityData = serviceForm.cityData || {};
      
      // Sync legacy field with new field + populate assetIds for backward compatibility
      const dataToSave = {
        ...serviceForm,
        // City fields
        city: cityData.city || null,
        state: cityData.state || null,
        country: 'USA', // Default to USA for now
        capacity: serviceForm.capacityPerResource, // Sync legacy capacity field
        assetIds: serviceForm.linkedAssets // Sync assetIds with linkedAssets for backward compatibility
      };
      
      // Remove the cityData object from the payload (backend expects flat fields)
      delete dataToSave.cityData;

      if (editingService) {
        await api.put(`/services/${editingService.id}`, dataToSave);
        toast.success("Service updated!");
      } else {
        await api.post('/services', dataToSave);
        toast.success("Service created!");
      }
      
      await loadServices();
      closeServiceModal();
      reloadPreview();
    } catch (error) {
      console.error("Error saving service:", error);
      
      // Handle Pydantic validation errors
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Check if it's an array of validation errors (Pydantic format)
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => {
            const field = err.loc ? err.loc.join(' → ') : 'Unknown field';
            return `${field}: ${err.msg}`;
          }).join('\n');
          toast.error(`Validation errors:\n${errorMessages}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error("Failed to save service");
        }
      } else {
        toast.error("Failed to save service");
      }
    }
  };

  const handleDeleteService = async (serviceId) => {
    
    if (!serviceId) {
      toast.error("Service ID is missing");
      console.error("Service ID is undefined or null");
      return;
    }
    
    if (!window.confirm("Are you sure you want to delete this service?")) {
      return;
    }
    
    try {
      const response = await api.delete(`/services/${serviceId}`);
      toast.success("Service deleted successfully");
      await loadServices();
      reloadPreview();
    } catch (error) {
      console.error("Error deleting service:", error);
      console.error("Error response:", error.response);
      toast.error(error.response?.data?.detail || "Failed to delete service");
    }
  };

  const addTimeSlot = () => {
    setServiceForm({
      ...serviceForm,
      availableTimeSlots: [...serviceForm.availableTimeSlots, "12:00"]
    });
  };

  const updateTimeSlot = (index, value) => {
    const newSlots = [...serviceForm.availableTimeSlots];
    newSlots[index] = value;
    setServiceForm({
      ...serviceForm,
      availableTimeSlots: newSlots
    });
  };

  const removeTimeSlot = (index) => {
    const newSlots = serviceForm.availableTimeSlots.filter((_, i) => i !== index);
    setServiceForm({
      ...serviceForm,
      availableTimeSlots: newSlots
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check if we already have 5 images
      if (serviceForm.images.length >= 5) {
        toast.error('Maximum 5 photos allowed');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setServiceForm({
          ...serviceForm,
          images: [...serviceForm.images, reader.result]
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index) => {
    setServiceForm({
      ...serviceForm,
      images: serviceForm.images.filter((_, i) => i !== index)
    });
  };

  const generateEmbedCode = () => {
    if (!orgData || !orgData.orgId) {
      return '<!-- Loading organization data... -->';
    }
    
    const widgetUrl = `${FRONTEND_URL}/widget-v3.html?org=${orgData.orgId}`;
    return `<!-- BookingFlows Booking Widget -->
<iframe 
  src="${widgetUrl}"
  width="100%"
  height="800"
  frameborder="0"
  style="border-radius: 16px; border: 1px solid #e5e7eb; max-width: 700px; margin: 0 auto; display: block;"
></iframe>`;
  };

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(generateEmbedCode());
    toast.success("Embed code copied to clipboard!");
  };

  if (loading) {
    return (
      <DashboardPageLayout
        title="Booking Widget Setup"
        subtitle="Create services, customize your widget, and get the embed code"
      >
        <div className="text-center py-12">Loading...</div>
      </DashboardPageLayout>
    );
  }

  return (
    <DashboardPageLayout
      title="Booking Widget Setup"
      subtitle="Create services, customize your widget, and get the embed code"
      guideIcon={GUIDE_CONTENT.services.icon}
      guideTitle={GUIDE_CONTENT.services.title}
      guideDescription={GUIDE_CONTENT.services.description}
      actions={
        <PrimaryButton onClick={() => setShowServiceModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Service
        </PrimaryButton>
      }
    >
      <div className="space-y-6">
      {/* STEP 1: Create Your Services */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full font-bold text-lg">
              1
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg">Create Your Services</CardTitle>
              <p className="text-sm text-gray-600 mt-1">Add the services customers can book (e.g., Sunset Cruise, Private Charter)</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {services.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
              <p className="text-gray-500">No services yet. Click "Add Service" above to create your first service.</p>
            </div>
          ) : (
            <>
              <div className="grid gap-3">
                {services
                  .slice((servicesPage - 1) * SERVICES_PER_PAGE, servicesPage * SERVICES_PER_PAGE)
                  .map((service) => {
                    // Debug logging
                    
                    return (
                  <div key={service.id} className="border rounded-lg p-3 flex items-start gap-3 hover:border-blue-400 transition-colors bg-white">
                    {service.images && service.images.length > 0 ? (
                      <img src={service.images[0]} alt={service.name} className="w-16 h-16 rounded object-cover" />
                    ) : (
                      <div className="w-16 h-16 rounded bg-gray-100 flex items-center justify-center text-gray-400">
                        <ImageIcon className="w-6 h-6" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold">{service.name}</h3>
                          <p className="text-xs text-gray-600 truncate">{service.description || "No description"}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="outline" size="sm" onClick={() => openServiceModal(service)} className="h-8 px-2">
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDeleteService(service.id)} className="text-red-600 hover:text-red-700 h-8 px-2">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-600">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {service.duration}min
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {service.capacity}
                        </span>
                        <span className="font-semibold text-blue-600 flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {service.price}
                        </span>
                        <span className="flex items-center gap-1">
                          <Settings className="w-3 h-3" />
                          {service.timezone || "CST"}
                        </span>
                        {service.serviceType && (
                          <span className="flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium">
                            {service.serviceType === "whole_asset" ? "Private Charter" : "Per Ticket"}
                          </span>
                        )}
                        {service.associatedAssets && service.associatedAssets.length > 0 && (
                          <span className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">
                            <Anchor className="w-3 h-3" />
                            {service.associatedAssets.length} asset{service.associatedAssets.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        <span className={service.active ? "text-green-600" : "text-gray-400"}>
                          {service.active ? "● Active" : "○ Inactive"}
                        </span>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Pagination for Services */}
              {services.length > SERVICES_PER_PAGE && (
                <div className="mt-4 flex items-center justify-between border-t pt-3">
                  <div className="text-sm text-gray-600">
                    Showing {((servicesPage - 1) * SERVICES_PER_PAGE) + 1} to {Math.min(servicesPage * SERVICES_PER_PAGE, services.length)} of {services.length} services
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setServicesPage(servicesPage - 1)}
                      disabled={servicesPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {[...Array(Math.ceil(services.length / SERVICES_PER_PAGE))].map((_, i) => (
                        <Button
                          key={i + 1}
                          variant={servicesPage === i + 1 ? "default" : "outline"}
                          size="sm"
                          onClick={() => setServicesPage(i + 1)}
                          className="w-8 h-8 p-0"
                        >
                          {i + 1}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setServicesPage(servicesPage + 1)}
                      disabled={servicesPage >= Math.ceil(services.length / SERVICES_PER_PAGE)}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* STEP 2: Get Embed Code */}
      <Card className="border-l-4 border-l-purple-500">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-purple-100 text-purple-700 rounded-full font-bold text-lg">
              2
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg">Get Embed Code</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Preview and copy embed code for your website
              </p>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Widget Type Selector & Preview */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm text-gray-700">Live Preview</h3>
              <a
                href={orgData?.orgId ? 
                  (previewWidget === "standard" ? `${FRONTEND_URL}/widget-v3.html?org=${btoa(orgData.orgId)}` :
                   previewWidget === "cards" ? `${FRONTEND_URL}/widget-cards.html?org=${btoa(orgData.orgId)}` :
                   previewWidget === "button" ? `${FRONTEND_URL}/widget-button.html?org=${btoa(orgData.orgId)}` :
                   previewWidget === "link" ? `${FRONTEND_URL}/widget-v3.html?org=${btoa(orgData.orgId)}` :
                   `${FRONTEND_URL}/widget-v3.html?org=${btoa(orgData.orgId)}`) : '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                Open full
              </a>
            </div>

            {/* Widget Type Selector */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setPreviewWidget("standard")}
                className={`px-3 py-2 text-xs font-medium rounded-lg transition-all ${
                  previewWidget === "standard"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Standard
              </button>
              <button
                onClick={() => setPreviewWidget("cards")}
                className={`px-3 py-2 text-xs font-medium rounded-lg transition-all ${
                  previewWidget === "cards"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Card View
              </button>
              <button
                onClick={() => setPreviewWidget("button")}
                className={`px-3 py-2 text-xs font-medium rounded-lg transition-all ${
                  previewWidget === "button"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Floating Button
              </button>
              <button
                onClick={() => setPreviewWidget("link")}
                className={`px-3 py-2 text-xs font-medium rounded-lg transition-all ${
                  previewWidget === "link"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Direct URL
              </button>
            </div>

            {/* Button Position Selector (only for floating button) */}
            {previewWidget === "button" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <Label className="text-xs font-semibold text-blue-900 mb-2 block">Button Position</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "top-left", label: "↖ Top Left" },
                    { value: "top-center", label: "↑ Top Center" },
                    { value: "top-right", label: "↗ Top Right" },
                    { value: "bottom-left", label: "↙ Bottom Left" },
                    { value: "bottom-center", label: "↓ Bottom Center" },
                    { value: "bottom-right", label: "↘ Bottom Right" }
                  ].map((pos) => (
                    <button
                      key={pos.value}
                      onClick={() => setButtonPosition(pos.value)}
                      className={`px-2 py-1.5 text-xs rounded transition-all ${
                        buttonPosition === pos.value
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pos.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Request Link Toggle */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <div>
                    <Label className="text-xs font-semibold text-purple-900 block">Custom Booking Requests</Label>
                    <p className="text-xs text-purple-700 mt-0.5">Allow customers to request custom bookings</p>
                  </div>
                </div>
                <ServiceToggle
                  checked={config.enableCustomRequests !== false}
                  onChange={(checked) => {
                    setConfig({ ...config, enableCustomRequests: checked });
                    saveConfig({ ...config, enableCustomRequests: checked });
                  }}
                />
              </div>
              {config.enableCustomRequests !== false && (
                <p className="text-xs text-purple-600 mt-2 pl-6">
                  Shows "Don't see what you're looking for?" link in your widget
                </p>
              )}
            </div>

            {/* Preview Frame */}
            {previewWidget === "link" ? (
              // Direct URL - No preview, just show the embed code section below
              <div className="rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center" style={{ height: "600px" }}>
                <div className="text-center p-8 max-w-md">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Direct URL</h3>
                  <p className="text-gray-600 mb-6">Copy the URL below to use in your website navigation, email campaigns, or social media</p>
                  <div className="bg-white border-2 border-blue-200 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Your Booking URL</p>
                    <p className="text-sm font-mono text-blue-600 break-all">{WIDGET_PRODUCTION_URL}/widget-v3.html?org={orgData?.orgId}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: "600px" }}>
                {orgData?.orgId ? (
                  <iframe
                    id="widget-preview"
                    src={
                      previewWidget === "standard"
                        ? `${FRONTEND_URL}/widget-v3.html?org=${btoa(orgData.orgId)}&preview=true`
                        : previewWidget === "cards"
                        ? `${FRONTEND_URL}/widget-cards.html?org=${btoa(orgData.orgId)}&preview=true`
                        : previewWidget === "button"
                        ? `${FRONTEND_URL}/widget-button.html?org=${btoa(orgData.orgId)}&preview=true`
                        : `${FRONTEND_URL}/widget-v3.html?org=${btoa(orgData.orgId)}&preview=true`
                    }
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    title="Booking Widget Preview"
                    key={`${previewWidget}-${orgData.orgId}-${buttonPosition}`}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full bg-gray-50">
                    <p className="text-gray-500">Loading preview...</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Embed Codes Section - Show ONLY selected view */}
          <div className="border-t pt-6">
            <h3 className="font-semibold text-base mb-3">Embed Code</h3>
            
            {previewWidget === "standard" && (
              <div className="bg-gray-50 rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">Standard Widget</h4>
                  <span className="text-xs text-gray-600">Clean booking flow</span>
                </div>
                <div className="relative">
                  <pre className="bg-white p-3 rounded text-xs overflow-x-auto border font-mono">
{`<iframe 
  src="${WIDGET_PRODUCTION_URL}/widget-v3.html?org=${orgData?.orgId || 'YOUR_ORG_ID'}"
  width="100%"
  height="800"
  frameborder="0"
></iframe>`}
                  </pre>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(`<iframe src="${WIDGET_PRODUCTION_URL}/widget-v3.html?org=${orgData?.orgId}" width="100%" height="800" frameborder="0"></iframe>`);
                      toast.success("Copied!");
                    }}
                    size="sm"
                    className="absolute top-2 right-2"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}

            {previewWidget === "cards" && (
              <div className="bg-gray-50 rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">Card/Grid View</h4>
                  <span className="text-xs text-gray-600">Image gallery</span>
                </div>
                <div className="relative">
                  <pre className="bg-white p-3 rounded text-xs overflow-x-auto border font-mono">
{`<iframe 
  src="${WIDGET_PRODUCTION_URL}/widget-cards.html?org=${orgData?.orgId || 'YOUR_ORG_ID'}"
  width="100%"
  height="900"
  frameborder="0"
></iframe>`}
                  </pre>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(`<iframe src="${WIDGET_PRODUCTION_URL}/widget-cards.html?org=${orgData?.orgId}" width="100%" height="900" frameborder="0"></iframe>`);
                      toast.success("Copied!");
                    }}
                    size="sm"
                    className="absolute top-2 right-2"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}

            {previewWidget === "button" && (
              <div className="bg-gray-50 rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">Floating Button</h4>
                  <span className="text-xs text-gray-600">Position: {buttonPosition}</span>
                </div>
                <div className="relative">
                  <pre className="bg-white p-3 rounded text-xs overflow-x-auto border font-mono">
{`<script>
  (function(){
    var f=document.createElement('iframe');
    f.src='${WIDGET_PRODUCTION_URL}/widget-button.html?org=${orgData?.orgId || 'YOUR_ORG_ID'}&position=${buttonPosition}';
    f.style.cssText='position:fixed;${
      buttonPosition === "top-left" ? "top:24px;left:24px;" :
      buttonPosition === "top-center" ? "top:24px;left:50%;transform:translateX(-50%);" :
      buttonPosition === "top-right" ? "top:24px;right:24px;" :
      buttonPosition === "bottom-left" ? "bottom:24px;left:24px;" :
      buttonPosition === "bottom-center" ? "bottom:24px;left:50%;transform:translateX(-50%);" :
      "bottom:24px;right:24px;"
    }width:200px;height:70px;border:none;z-index:9999;background:transparent;';
    document.body.appendChild(f);
  })();
</script>`}
                  </pre>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(`<script>(function(){var f=document.createElement('iframe');f.src='${WIDGET_PRODUCTION_URL}/widget-button.html?org=${orgData?.orgId}&position=${buttonPosition}';f.style.cssText='position:fixed;${
                        buttonPosition === "top-left" ? "top:24px;left:24px;" :
                        buttonPosition === "top-center" ? "top:24px;left:50%;transform:translateX(-50%);" :
                        buttonPosition === "top-right" ? "top:24px;right:24px;" :
                        buttonPosition === "bottom-left" ? "bottom:24px;left:24px;" :
                        buttonPosition === "bottom-center" ? "bottom:24px;left:50%;transform:translateX(-50%);" :
                        "bottom:24px;right:24px;"
                      }width:200px;height:70px;border:none;z-index:9999;background:transparent;';document.body.appendChild(f);})();</script>`);
                      toast.success("Copied!");
                    }}
                    size="sm"
                    className="absolute top-2 right-2"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}

            {previewWidget === "link" && (
              <div className="bg-gray-50 rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">Direct URL</h4>
                  <span className="text-xs text-gray-600">Use in links, buttons, menus</span>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    readOnly
                    value={`${WIDGET_PRODUCTION_URL}/widget-v3.html?org=${orgData?.orgId || 'YOUR_ORG_ID'}`}
                    className="w-full px-3 py-2.5 text-sm border rounded bg-white pr-16 font-mono"
                    onClick={(e) => e.target.select()}
                  />
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(`${WIDGET_PRODUCTION_URL}/widget-v3.html?org=${orgData?.orgId}`);
                      toast.success("Copied!");
                    }}
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {/* Branding Note */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-900">
              <strong>💡 Customize colors & logo:</strong> Go to the <strong>Branding</strong> tab. Changes apply to all widgets automatically.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* STEP 3: Done */}
      <Card className="border-l-4 border-l-green-500">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 text-green-700 rounded-full font-bold text-lg">
              3
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg">You're All Set!</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Bookings will appear in your Bookings tab
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              Customer bookings will automatically appear in your <strong>Bookings</strong> tab with payment status and customer details.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Service Modal - Multi-Step Wizard */}
      <Dialog open={showServiceModal} onOpenChange={(open) => !open && closeServiceModal()}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingService ? "Edit Service" : "Create Service"}
            </DialogTitle>
          </DialogHeader>

          {/* Progress Bar */}
          <div className="flex items-center gap-2">
            {Array.from({ length: totalServiceSteps }, (_, i) => (
              <div
                key={i}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  i + 1 <= serviceModalStep ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          <p className="text-sm text-gray-500 -mt-2">Step {serviceModalStep} of {totalServiceSteps}</p>

          {/* Content - CLEAN SIMPLE DESIGN */}
          <div className="flex-1 overflow-y-auto -mx-6 px-6" style={{ WebkitOverflowScrolling: 'touch' }}>
              {/* STEP 1: THE BASICS */}
              {serviceModalStep === 1 && (
                <div className="space-y-6 py-2">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Service Name</Label>
                      <Input
                        value={serviceForm.name}
                        onChange={(e) => setServiceForm({...serviceForm, name: e.target.value})}
                        placeholder="Sunset Cruise, Hot Stone Massage, VIP Table Rental"
                        className="mt-1.5 h-12 text-base"
                      />
                    </div>

                    {/* Active/Inactive Toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Service Status</Label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {serviceForm.active ? "Service is visible and bookable" : "Service is hidden from customers"}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setServiceForm({...serviceForm, active: !serviceForm.active})}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          serviceForm.active ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            serviceForm.active ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Description</Label>
                      <textarea
                        value={serviceForm.description}
                        onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                        placeholder="Experience the beautiful sunset over Lake Austin on our luxury yacht. Includes complimentary beverages and snacks."
                        className="w-full px-3 py-3 border border-gray-300 rounded-md mt-1.5 text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={3}
                      />
                      <p className="text-xs text-gray-500 mt-1">Optional</p>
                    </div>

                    {/* Category Dropdown for Marketplace */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Tag className="w-4 h-4 text-gray-500" />
                        Category <span className="text-red-500">*</span>
                      </Label>
                      <Select 
                        value={serviceForm.category || ''} 
                        onValueChange={(value) => setServiceForm({...serviceForm, category: value})}
                      >
                        <SelectTrigger className="mt-1.5 h-12 text-base" data-testid="service-category-select">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORY_OPTIONS.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <span className="flex items-center gap-2">
                                <span>{cat.icon}</span>
                                <span>{cat.name}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">Required for marketplace listing</p>
                    </div>

                    {/* City Selection */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        City <span className="text-red-500">*</span>
                      </Label>
                      <div className="mt-1.5">
                        <CitySelect
                          value={serviceForm.cityData}
                          onChange={(city) => setServiceForm({...serviceForm, cityData: city})}
                          placeholder="Search for a city..."
                          required={true}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Select the city where this experience takes place</p>
                    </div>

                    {/* Meeting Location (Specific Address) */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        Meeting Point Address
                      </Label>
                      <Input
                        value={serviceForm.meetingLocation}
                        onChange={(e) => setServiceForm({...serviceForm, meetingLocation: e.target.value})}
                        placeholder="e.g., 123 Main St or Austin Marina, Dock B"
                        className="mt-1.5 h-12 text-base"
                        data-testid="service-meeting-location"
                      />
                      <p className="text-xs text-gray-500 mt-1">Specific address where customers should meet</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Price</Label>
                        <div className="relative mt-1.5">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-base">$</span>
                          <Input
                            type="number"
                            value={serviceForm.price}
                            onChange={(e) => setServiceForm({...serviceForm, price: parseFloat(e.target.value) || 0})}
                            placeholder="299"
                            className="pl-7 h-12 text-base"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-gray-700">Duration</Label>
                        {/* Duration selector - changes based on multi-day toggle */}
                        {!serviceForm.isMultiDay ? (
                          <select
                            value={serviceForm.duration}
                            onChange={(e) => setServiceForm({...serviceForm, duration: parseInt(e.target.value)})}
                            className="w-full px-3 h-12 border border-gray-300 rounded-md mt-1.5 text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            style={{ fontSize: '16px' }}
                          >
                            <option value={30}>30 minutes</option>
                            <option value={60}>1 hour</option>
                            <option value={90}>1.5 hours</option>
                            <option value={120}>2 hours</option>
                            <option value={150}>2.5 hours</option>
                            <option value={180}>3 hours</option>
                            <option value={240}>4 hours</option>
                            <option value={300}>5 hours</option>
                            <option value={360}>6 hours</option>
                            <option value={420}>7 hours</option>
                            <option value={480}>8 hours</option>
                            <option value={600}>10 hours</option>
                            <option value={720}>12 hours</option>
                          </select>
                        ) : (
                          <div className="flex gap-2 mt-1.5">
                            <select
                              value={serviceForm.durationDays}
                              onChange={(e) => {
                                const days = parseInt(e.target.value);
                                setServiceForm({
                                  ...serviceForm,
                                  durationDays: days,
                                  duration: days * 1440 // Convert days to minutes
                                });
                              }}
                              className="flex-1 px-3 h-12 border border-gray-300 rounded-md text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                              style={{ fontSize: '16px' }}
                            >
                              <option value={1}>1 day</option>
                              <option value={2}>2 days</option>
                              <option value={3}>3 days</option>
                              <option value={4}>4 days</option>
                              <option value={5}>5 days</option>
                              <option value={6}>6 days</option>
                              <option value={7}>7 days</option>
                              <option value={0}>7+ days (custom)</option>
                            </select>
                            {serviceForm.durationDays === 0 && (
                              <Input
                                type="number"
                                placeholder="Days"
                                min="8"
                                value={serviceForm.duration > 10080 ? Math.floor(serviceForm.duration / 1440) : 8}
                                onChange={(e) => {
                                  const days = parseInt(e.target.value) || 8;
                                  setServiceForm({
                                    ...serviceForm,
                                    duration: days * 1440
                                  });
                                }}
                                className="w-24 h-12 text-base"
                              />
                            )}
                          </div>
                        )}
                        {/* Multi-day toggle - clean placement */}
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="checkbox"
                            id="isMultiDay"
                            checked={serviceForm.isMultiDay}
                            onChange={(e) => {
                              const isMultiDay = e.target.checked;
                              setServiceForm({
                                ...serviceForm,
                                isMultiDay,
                                duration: isMultiDay ? 1440 : 180, // Default to 1 day (1440 min) or 3 hours
                                durationDays: isMultiDay ? 1 : 0
                              });
                            }}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <label htmlFor="isMultiDay" className="text-sm text-gray-600 cursor-pointer">
                            Multi-day event
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Show start time for multi-day events */}
                    {serviceForm.isMultiDay && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <Label className="text-sm font-medium text-gray-700">Start Time</Label>
                        <select
                          value={serviceForm.startTime}
                          onChange={(e) => setServiceForm({...serviceForm, startTime: e.target.value})}
                          className="w-full px-3 h-12 border border-gray-300 rounded-md mt-1.5 text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          style={{ fontSize: '16px' }}
                        >
                          <option value="06:00">6:00 AM</option>
                          <option value="07:00">7:00 AM</option>
                          <option value="08:00">8:00 AM</option>
                          <option value="09:00">9:00 AM</option>
                          <option value="10:00">10:00 AM</option>
                          <option value="11:00">11:00 AM</option>
                          <option value="12:00">12:00 PM</option>
                          <option value="13:00">1:00 PM</option>
                          <option value="14:00">2:00 PM</option>
                          <option value="15:00">3:00 PM</option>
                          <option value="16:00">4:00 PM</option>
                          <option value="17:00">5:00 PM</option>
                        </select>
                        <p className="text-xs text-gray-600 mt-1">
                          What time does the event start on Day 1?
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Timezone</Label>
                        <select
                          value={serviceForm.timezone}
                          onChange={(e) => setServiceForm({...serviceForm, timezone: e.target.value})}
                          className="w-full px-3 h-12 border border-gray-300 rounded-md mt-1.5 text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          style={{ fontSize: '16px' }}
                        >
                          <option value="CST">CST (Central)</option>
                          <option value="EST">EST (Eastern)</option>
                          <option value="MST">MST (Mountain)</option>
                          <option value="PST">PST (Pacific)</option>
                          <option value="AKST">AKST (Alaska)</option>
                          <option value="HST">HST (Hawaii)</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Customers will see times in this timezone</p>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-gray-700">Minimum Notice</Label>
                        <select
                          value={serviceForm.minimumNoticeHours || 2}
                          onChange={(e) => setServiceForm({...serviceForm, minimumNoticeHours: parseInt(e.target.value)})}
                          className="w-full px-3 h-12 border border-gray-300 rounded-md mt-1.5 text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          style={{ fontSize: '16px' }}
                        >
                          <option value={0}>Instant booking</option>
                          <option value={2}>2 hours ahead</option>
                          <option value={4}>4 hours ahead</option>
                          <option value={12}>12 hours ahead</option>
                          <option value={24}>1 day ahead</option>
                          <option value={48}>2 days ahead</option>
                          <option value={168}>1 week ahead</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">
                        Fall Back Booking Contact Number <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="tel"
                        value={serviceForm.emergencyContactNumber || ''}
                        onChange={(e) => setServiceForm({...serviceForm, emergencyContactNumber: e.target.value})}
                        placeholder="(512) 555-1234"
                        className={`mt-1.5 h-12 text-base ${
                          !serviceForm.emergencyContactNumber || serviceForm.emergencyContactNumber.trim() === ''
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300'
                        }`}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Number customers call if online booking fails or they need help</p>
                      {(!serviceForm.emergencyContactNumber || serviceForm.emergencyContactNumber.trim() === '') && (
                        <p className="text-xs text-red-600 mt-1">⚠️ Required to proceed to next step</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: ASSET SELECTION - REQUIRED! */}
              {serviceModalStep === 2 && (
                <div className="space-y-6 py-2">
                  <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-1">What limits capacity for this service?</h3>
                    <p className="text-sm text-blue-800">
                      Select the assets (boats, chairs, studios, etc.) that provide this service. 
                      Their capacity determines how many people can book.
                    </p>
                  </div>

                  {/* Asset Selection - REQUIRED */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-gray-700">Select Assets <span className="text-red-500">*</span></Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (onNavigate) {
                            closeServiceModal();
                            onNavigate('assets');
                            toast.success('Switched to Assets tab - create your asset here!', { duration: 4000 });
                          }
                        }}
                        className="text-sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Create New Asset
                      </Button>
                    </div>

                    {!assets || assets.length === 0 ? (
                      <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                        <p className="font-semibold text-red-900">⚠️ No assets found</p>
                        <p className="text-sm text-red-800 mt-1">
                          You need to create at least one asset before creating a service.
                        </p>
                        <Button
                          type="button"
                          onClick={() => {
                            if (onNavigate) {
                              closeServiceModal();
                              onNavigate('assets');
                              toast.success('Switched to Assets tab - create your first asset!', { duration: 4000 });
                            }
                          }}
                          className="mt-3 bg-red-600 hover:bg-red-700 text-white"
                        >
                          Go to Assets Tab
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-2 max-h-80 overflow-y-auto bg-gray-50 border-2 border-gray-200 rounded-lg p-3">
                          {assets.map((asset) => {
                            const isSelected = (serviceForm.linkedAssets || []).includes(asset.id);
                            return (
                              <label
                                key={asset.id}
                                className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-all ${
                                  isSelected 
                                    ? 'bg-blue-100 border-2 border-blue-600' 
                                    : 'bg-white border-2 border-gray-300 hover:border-blue-400'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {
                                    const newLinkedAssets = isSelected
                                      ? (serviceForm.linkedAssets || []).filter(id => id !== asset.id)
                                      : [...(serviceForm.linkedAssets || []), asset.id];
                                    setServiceForm({...serviceForm, linkedAssets: newLinkedAssets});
                                  }}
                                  className="w-5 h-5 text-blue-600 rounded"
                                />
                                <div className="flex-1">
                                  <div className="font-semibold text-base">{asset.name}</div>
                                  <div className="text-sm text-gray-600">
                                    Capacity: <span className="font-medium">{asset.capacity || 'Not set'}</span> people
                                    {asset.type && ` • Type: ${asset.type}`}
                                  </div>
                                </div>
                                {isSelected && <Check className="w-6 h-6 text-blue-600" />}
                              </label>
                            );
                          })}
                        </div>

                        {/* Selection Status */}
                        {(serviceForm.linkedAssets || []).length === 0 ? (
                          <div className="bg-red-50 border border-red-300 rounded p-3">
                            <p className="text-sm text-red-800 font-medium">
                              ⚠️ Please select at least one asset
                            </p>
                          </div>
                        ) : (
                          <div className="bg-green-50 border border-green-300 rounded p-3">
                            <p className="text-sm text-green-800 font-medium">
                              ✓ {(serviceForm.linkedAssets || []).length} asset{(serviceForm.linkedAssets || []).length !== 1 ? 's' : ''} selected
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="border-t border-gray-200 my-6"></div>

                  {/* Customer Asset Selection Option */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-purple-300 transition-all">
                      <input
                        type="checkbox"
                        id="allowCustomerSelection"
                        checked={serviceForm.allowCustomerAssetSelection || false}
                        onChange={(e) => {
                          setServiceForm({
                            ...serviceForm, 
                            allowCustomerAssetSelection: e.target.checked
                          });
                        }}
                        className="w-5 h-5 mt-0.5 text-purple-600 rounded"
                      />
                      <label htmlFor="allowCustomerSelection" className="flex-1 cursor-pointer">
                        <div className="font-medium text-base">Let customers choose which asset</div>
                        <p className="text-sm text-gray-600 mt-1">
                          Show asset selector in widget. Example: Customer picks "Shawn's Chair" or "Double Decker Boat"
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          If unchecked: You assign after booking or use auto-scheduler
                        </p>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: SCHEDULE & BOOKING TYPE */}
              {serviceModalStep === 3 && (
                <div className="space-y-8 py-2">
                  {/* Booking Type */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">How can customers book?</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setServiceForm({...serviceForm, bookingMode: "per_ticket", serviceType: "per_ticket"})}
                        className={`p-4 rounded-lg border-2 transition-all text-left ${
                          serviceForm.bookingMode === "per_ticket"
                            ? "border-blue-600 bg-blue-50"
                            : "border-gray-300 bg-white hover:border-blue-400"
                        }`}
                      >
                        <div className="font-medium text-base">Sell Individual Tickets</div>
                        <p className="text-sm text-gray-600 mt-1">Multiple people can book same time</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setServiceForm({...serviceForm, bookingMode: "buyout", serviceType: "whole_asset"})}
                        className={`p-4 rounded-lg border-2 transition-all text-left ${
                          serviceForm.bookingMode === "buyout"
                            ? "border-blue-600 bg-blue-50"
                            : "border-gray-300 bg-white hover:border-blue-400"
                        }`}
                      >
                        <div className="font-medium text-base">Exclusive/Private</div>
                        <p className="text-sm text-gray-600 mt-1">Only one group at a time</p>
                      </button>
                    </div>
                  </div>

                  <div className="border-t border-gray-200"></div>

                  {/* Available Time Slots - Different for single-day vs multi-day */}
                  <div className="space-y-3">
                    {!serviceForm.isMultiDay ? (
                      <>
                        <Label className="text-sm font-medium text-gray-700">Available Time Slots</Label>
                        <p className="text-xs text-gray-600">When can customers book this service?</p>
                        <MultiTimeSlotSelector
                          selectedSlots={serviceForm.availableTimeSlots}
                          onChange={(slots) => setServiceForm({...serviceForm, availableTimeSlots: slots})}
                        />
                      </>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <Label className="text-sm font-medium text-gray-700">Multi-Day Event Schedule</Label>
                        <p className="text-sm text-gray-600 mt-2">
                          <strong>Start Time:</strong> {serviceForm.startTime} {serviceForm.timezone}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          <strong>Duration:</strong> {serviceForm.durationDays || Math.floor(serviceForm.duration / 1440)} days
                        </p>
                        <p className="text-xs text-gray-500 mt-3">
                          💡 Customers will select a start date, and the event will block all {serviceForm.durationDays || Math.floor(serviceForm.duration / 1440)} consecutive days.
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          💡 Set available start dates in Step 3 (schedule section).
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-200"></div>

                  {/* Optional: Custom Questions & Add-Ons (Collapsed) */}
                  <details className="group">
                    <summary className="cursor-pointer font-medium text-gray-700 flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
                      <span className="flex items-center gap-2">
                        <Edit className="w-4 h-4" />
                        Custom Questions & Add-Ons
                      </span>
                      <span className="text-gray-500 text-sm">Optional - Click to expand</span>
                    </summary>
                    <div className="mt-4 space-y-6 p-4 bg-white border border-gray-200 rounded-lg">
                      {/* Add-ons */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Add-Ons (Optional Extras)</Label>
                            <p className="text-xs text-gray-500 mt-0.5">Upsell extras like catering, photos, equipment</p>
                          </div>
                        </div>
                        {serviceForm.addOns.map((addon, index) => (
                          <div key={index} className="flex gap-2 items-center bg-gray-50 p-3 rounded-lg border border-gray-200">
                            <Input
                              placeholder="Name (e.g., Catering)"
                              value={addon.name}
                              onChange={(e) => {
                                const newAddOns = [...serviceForm.addOns];
                                newAddOns[index].name = e.target.value;
                                setServiceForm({...serviceForm, addOns: newAddOns});
                              }}
                              className="flex-1"
                            />
                            <div className="relative w-28">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <Input
                                type="number"
                                placeholder="150"
                                value={addon.price}
                                onChange={(e) => {
                                  const newAddOns = [...serviceForm.addOns];
                                  newAddOns[index].price = parseFloat(e.target.value) || 0;
                                  setServiceForm({...serviceForm, addOns: newAddOns});
                                }}
                                className="pl-6"
                                step="0.01"
                                min="0"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newAddOns = serviceForm.addOns.filter((_, i) => i !== index);
                                setServiceForm({...serviceForm, addOns: newAddOns});
                              }}
                            >
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-center"
                          onClick={() => {
                            setServiceForm({
                              ...serviceForm,
                              addOns: [...serviceForm.addOns, { id: Date.now().toString(), name: '', price: 0 }]
                            });
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Extra
                        </Button>
                      </div>

                      <div className="border-t border-gray-200"></div>

                      {/* Custom Questions */}
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm font-medium text-gray-700">Custom Questions</Label>
                          <p className="text-xs text-gray-500 mt-0.5">Ask industry-specific questions during booking</p>
                        </div>
                        {serviceForm.customQuestions.map((q, index) => (
                          <div key={index} className="flex gap-2 items-center bg-gray-50 p-3 rounded-lg border border-gray-200">
                            <Input
                              placeholder="Question (e.g., Any dietary restrictions?)"
                              value={q.question}
                              onChange={(e) => {
                                const newQuestions = [...serviceForm.customQuestions];
                                newQuestions[index].question = e.target.value;
                                setServiceForm({...serviceForm, customQuestions: newQuestions});
                              }}
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newQuestions = serviceForm.customQuestions.filter((_, i) => i !== index);
                                setServiceForm({...serviceForm, customQuestions: newQuestions});
                              }}
                            >
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-center"
                          onClick={() => {
                            setServiceForm({
                              ...serviceForm,
                              customQuestions: [...serviceForm.customQuestions, { question: '', type: 'text', required: false }]
                            });
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Question
                        </Button>
                      </div>
                    </div>
                  </details>
                </div>
              )}
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center justify-between pt-4 border-t -mx-6 px-6 -mb-6 pb-6 bg-gray-50">
              <Button
                variant="outline"
                onClick={prevServiceStep}
                disabled={serviceModalStep === 1}
                className="flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>

              {serviceModalStep === totalServiceSteps ? (
                <Button
                  onClick={handleSaveService}
                  className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                >
                  {editingService ? "Update Service" : "Create Service"}
                  <Check className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={nextServiceStep}
                  className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              )}
            </div>
        </DialogContent>
      </Dialog>
      </div> {/* Closes space-y-6 container */}
    </DashboardPageLayout>
  );
};

export default WidgetCustomization;
